import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { onSnapshot } from 'firebase/firestore';
import { startOfDay } from 'date-fns';
import { useAuth } from '../features/auth/useAuth';
import { useManagedLocations } from '../features/auth/useManagedLocations';
import { useLeaveActions } from '../features/leave/hooks/useLeaveActions';
import {
  buildLeaveRequestsQuery,
  buildPendingLeaveRequestsQuery,
  mapLeaveRequestSnapshot,
} from '../features/leave/services/leaveService';
import { LeaveRequestRecord, LeaveRequestStatus } from '../features/leave/types';
import { useSnackbar } from '../shared/utils/snackbar';
import { useDeferredActions } from '../shared/utils/useDeferredActions';
import { Notification, useNotifications } from './NotificationContext';

/** How long an Approve or Deny can be undone before it is written. */
export const DECISION_UNDO_MS = 6000;

export type LeaveDecisionVerb = 'APPROVED' | 'DENIED';

export interface LeaveDecision {
  verb: LeaveDecisionVerb;
  request: LeaveRequestRecord;
  comment?: string;
  /** When the decision was made (ms), so a reopened panel shows the time actually left to undo. */
  heldAt: number;
}

export type QueueItem =
  | { kind: 'leave'; id: string; request: LeaveRequestRecord; decision?: LeaveDecision }
  | { kind: 'nomination'; id: string; notification: Notification };

export type QueueStatus = 'idle' | 'loading' | 'ready' | 'error';

interface DecisionQueueContextType {
  /** Managers with a location, and admins: the people who can approve leave. */
  canDecideLeave: boolean;
  /**
   * Leave awaiting a decision (upcoming soonest start first, then any whose
   * dates have passed, most recent first), then unread success nominations.
   */
  items: QueueItem[];
  /** Items still waiting on someone; decisions held for Undo no longer count. */
  waitingCount: number;
  leaveStatus: QueueStatus;
  retry: () => void;
  approveLeave: (request: LeaveRequestRecord) => void;
  denyLeave: (request: LeaveRequestRecord, comment?: string) => void;
  undoDecision: (requestId: string) => void;
}

const DecisionQueueContext = createContext<DecisionQueueContextType | undefined>(undefined);

export const useDecisionQueue = () => {
  const context = useContext(DecisionQueueContext);
  if (!context) {
    throw new Error('useDecisionQueue must be used within a DecisionQueueProvider');
  }
  return context;
};

interface LeaveScope {
  role: string | null | undefined;
  uid: string | undefined;
  locations: readonly string[];
}

/** Whether a pending request belongs in this person's queue. */
export const isInLeaveScope = (request: LeaveRequestRecord, { role, uid, locations }: LeaveScope): boolean => {
  // Nobody approves their own leave.
  if (!uid || request.staff?.uid === uid) return false;
  if (role === 'ADMIN') return true;
  if (role === 'MANAGER') {
    return (request.visibility?.managerLocations || []).some((location) => locations.includes(location));
  }
  return false;
};

const startMillis = (request: LeaveRequestRecord) => request.period?.startDate?.toMillis?.() ?? 0;
const endMillis = (request: LeaveRequestRecord) => request.period?.endDate?.toMillis?.() ?? startMillis(request);

/**
 * Upcoming leave (ending today or later) soonest start first, then leave whose
 * dates have passed, most recent first. An undecided backlog from months ago
 * still needs a decision, but shouldn't bury what is about to happen.
 */
const byUrgency = (todayMillis: number) => (a: LeaveRequestRecord, b: LeaveRequestRecord) => {
  const aPassed = endMillis(a) < todayMillis;
  const bPassed = endMillis(b) < todayMillis;
  if (aPassed !== bPassed) return aPassed ? 1 : -1;
  return aPassed ? endMillis(b) - endMillis(a) : startMillis(a) - startMillis(b);
};

const AWAITING: LeaveRequestStatus[] = ['PENDING', 'CHANGE_PENDING'];

/**
 * Admins read every pending request. Managers read only their own locations,
 * in the location + status + year shape Leave Manager's team tab already uses,
 * which the existing composite index serves. Pending leave is assumed to start
 * between last year (a December request still undecided in January) and next.
 */
const pendingLeaveQueries = (role: string | null | undefined, locations: string[]) => {
  if (role === 'ADMIN') return [buildPendingLeaveRequestsQuery()];
  const year = new Date().getFullYear();
  return AWAITING.flatMap((status) =>
    [year - 1, year, year + 1].map((periodYear) =>
      buildLeaveRequestsQuery({ role: 'MANAGER', managerLocations: locations, status, year: periodYear })
    )
  );
};

/**
 * "Waiting on you" is read from the leave requests themselves rather than from
 * notification documents, so a request leaves every manager's queue as soon as
 * anyone decides it. Success nominations have no reviewed state of their own,
 * so they wait until this person opens them.
 */
export const DecisionQueueProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, userRole } = useAuth();
  const { notifications } = useNotifications();
  const { approveLeaveRequest, denyLeaveRequest } = useLeaveActions();
  const { enqueueSnackbar } = useSnackbar();

  const uid = user?.uid;
  const locations = useManagedLocations();
  const canDecideLeave = Boolean(uid) && (userRole === 'ADMIN' || (userRole === 'MANAGER' && locations.length > 0));

  const [pendingRequests, setPendingRequests] = useState<LeaveRequestRecord[]>([]);
  const [leaveStatus, setLeaveStatus] = useState<QueueStatus>('idle');
  const [attempt, setAttempt] = useState(0);
  // Decisions being written: hidden so the row doesn't flash back while the write lands.
  const [writing, setWriting] = useState<Record<string, true>>({});
  const livePendingIds = useRef(new Set<string>());

  useEffect(() => {
    if (!canDecideLeave) {
      setPendingRequests([]);
      livePendingIds.current = new Set();
      setLeaveStatus('idle');
      return undefined;
    }

    setLeaveStatus('loading');
    const queries = pendingLeaveQueries(userRole, locations);
    const results: LeaveRequestRecord[][] = queries.map(() => []);
    const delivered = new Set<number>();
    const unsubscribes = queries.map((pendingQuery, index) =>
      onSnapshot(
        pendingQuery,
        (snapshot) => {
          results[index] = snapshot.docs.map(mapLeaveRequestSnapshot);
          delivered.add(index);
          const merged = Array.from(new Map(results.flat().map((request) => [request.id, request])).values());
          livePendingIds.current = new Set(merged.map((request) => request.id));
          setPendingRequests(merged);
          if (delivered.size === queries.length) setLeaveStatus('ready');
        },
        (error) => {
          console.error('Error subscribing to pending leave requests:', error);
          setLeaveStatus('error');
        }
      )
    );
    return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
  }, [canDecideLeave, uid, userRole, locations, attempt]);

  const {
    pending: heldDecisions,
    schedule: holdDecision,
    cancel: undoDecision,
  } = useDeferredActions<LeaveDecision>(DECISION_UNDO_MS, (requestId, decision) => {
    // Decided by another manager, or withdrawn or changed by the requester.
    // (The write is also guarded server-side, for decisions that cross in flight.)
    if (!livePendingIds.current.has(requestId)) {
      enqueueSnackbar(
        `${decision.request.staff.displayName}’s request changed before your decision was saved, so nothing was recorded.`,
        { variant: 'info' }
      );
      return undefined;
    }

    setWriting((current) => ({ ...current, [requestId]: true }));
    const params = { request: decision.request, status: decision.verb, managerComment: decision.comment };
    const write = decision.verb === 'APPROVED' ? approveLeaveRequest(params) : denyLeaveRequest(params);
    // useLeaveActions reports failures itself; a failed request reappears from the live query.
    return write.finally(() =>
      setWriting((current) => {
        const next = { ...current };
        delete next[requestId];
        return next;
      })
    );
  });

  const approveLeave = useCallback(
    (request: LeaveRequestRecord) => holdDecision(request.id, { verb: 'APPROVED', request, heldAt: Date.now() }),
    [holdDecision]
  );
  const denyLeave = useCallback(
    (request: LeaveRequestRecord, comment?: string) =>
      holdDecision(request.id, {
        verb: 'DENIED',
        request,
        comment: comment?.trim() || undefined,
        heldAt: Date.now(),
      }),
    [holdDecision]
  );

  const items = useMemo<QueueItem[]>(() => {
    const leave: QueueItem[] = pendingRequests
      .filter((request) => isInLeaveScope(request, { role: userRole, uid, locations }))
      .filter((request) => !writing[request.id])
      .sort(byUrgency(startOfDay(new Date()).getTime()))
      .map((request) => ({ kind: 'leave', id: request.id, request, decision: heldDecisions[request.id] }));

    const nominations: QueueItem[] = notifications
      .filter((notification) => notification.type === 'success_nomination' && !notification.read)
      .map((notification) => ({ kind: 'nomination', id: notification.id, notification }));

    return [...leave, ...nominations];
  }, [pendingRequests, userRole, uid, locations, writing, heldDecisions, notifications]);

  const waitingCount = items.filter((item) => !(item.kind === 'leave' && item.decision)).length;

  const value: DecisionQueueContextType = {
    canDecideLeave,
    items,
    waitingCount,
    leaveStatus,
    retry: () => setAttempt((count) => count + 1),
    approveLeave,
    denyLeave,
    undoDecision,
  };

  return <DecisionQueueContext.Provider value={value}>{children}</DecisionQueueContext.Provider>;
};
